import { format, parseISO } from 'date-fns'

import type { PluginPingDetail } from '../../common/types/monitoring'
import type { Teams } from '../../common/utils/UtilityWrapper/types'

export interface ComputerWithTeam {
  teamId: string
  teamName: string
  computerName: string
  ipAddress: string
  count: number
}

export interface TeamComputersGroup {
  teamId: string
  teamName: string
  computers: ComputerWithTeam[]
}

export const formatBarDate = (dateStr: string): string =>
  dateStr ? format(parseISO(dateStr), 'EEE, MMM d') : ''

export const getDayTimestampRange = (dateStr: string) => ({
  start_timestamp: `${dateStr}T00:00:00.000Z`,
  end_timestamp: `${dateStr}T23:59:59.999Z`,
})

export const mapAndSortComputers = (
  items: PluginPingDetail[] = [],
  teams: Teams,
  teamId?: string,
): ComputerWithTeam[] => {
  const filtered = teamId ? items.filter((item) => item.team_id === teamId) : items

  return filtered
    .map((item) => ({
      teamId: item.team_id ?? 'unknown',
      teamName: (item.team_id && teams[item.team_id]?.name) || 'Unassigned',
      computerName: item.computer_name ?? 'Unknown',
      ipAddress: item.ip_address ?? '-',
      count: item.count,
    }))
    .sort(
      (a, b) =>
        a.teamName.localeCompare(b.teamName) || a.computerName.localeCompare(b.computerName),
    )
}

export const getNotConnectedComputers = (
  activeDateComputers: ComputerWithTeam[],
  diffDateComputers: ComputerWithTeam[],
): ComputerWithTeam[] => {
  const activeIps = new Set(activeDateComputers.map((computer) => computer.ipAddress))
  return diffDateComputers.filter((computer) => !activeIps.has(computer.ipAddress))
}

export const groupComputersByTeam = (computers: ComputerWithTeam[]): TeamComputersGroup[] => {
  const groups = new Map<string, TeamComputersGroup>()

  computers.forEach((computer) => {
    const group = groups.get(computer.teamId)
    if (group) {
      group.computers.push(computer)
    } else {
      groups.set(computer.teamId, {
        teamId: computer.teamId,
        teamName: computer.teamName,
        computers: [computer],
      })
    }
  })

  return [...groups.values()]
}
